import React from "react"
import Layout from "../../components/layout"
import Image from "../../components/image"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import Hero from "../../components/hero"
import { PageColumns, Column } from "../../components/page-column"
import MobileOnly from "../../components/mobile-only"
import StaticChart from "../../components/static-chart"
import ChartIncomeModelDesktop from "../../images/who-we-are/resources/income-model-desktop.inline.svg"
import ChartIncomeModelMobile from "../../images/who-we-are/resources/income-model-mobile.inline.svg"
import ChartMultipleRowsDesktop from "../../images/who-we-are/resources/mutiple-roles-desktop.inline.svg"
import ChartMultipleRowsMobile from "../../images/who-we-are/resources/mutiple-roles-mobile.inline.svg"
import Table from "../../components/table"
import ArrearsData from "../../data/tables/arrears.json"
import ReadNext from "../../components/read-next"

const PAGESLUG = "resources"

const table3_1 = (
  <Table
    data={ArrearsData}
    tableNumber="3.1"
    title="Arrears to the IMF of Countries with Obligations Overdue by Six Months or More and by Type, as of April 30, 2021"
    subTitle="(millions of SDRs)"
    source="Sudan cleared its arrears to the IMF on June 29, 2021 (after this report was finalized). <br/> For more information, visit <a href='https://www.imf.org/sudan'>www.imf.org/sudan</a>."
  />
)

export default function Resources() {
  return (
    <Layout>
      <section className="page page--pullBottom page--margin-medium">
        <Hero title="Resources" />

        <PageColumns>
          <Column type="media">
            <Image path="who-we-are/resources/resources-1.jpg" />
          </Column>

          <Column type="text">
            <h2>Budget</h2>
            <p>
              Implementation of the FY 2021 budget was affected by the onset of
              the COVID-19 crisis following the budget’s formulation. Total net
              administrative expenditures were $1,126 million, or 94.9 percent,
              of the approved structural budget of $1,186 million. The
              underspending relative to the structural budget reflects the
              travel moratorium, the impact of lower building occupancy, and
              IT-related shortfalls. The bulk of travel resources were
              reallocated to support increased crisis staffing, with ramp up
              continuing into FY 2022. Executive Board approval in April 2021 of
              a temporary increase in the general carryforward limit from 5
              percent to 8 percent made a total of $88.4 million in temporary
              resources available for general use to address crisis needs during
              the medium-term budget period.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <p>
              Externally financed capacity development activities fell by $50
              million in FY 2021 from FY 2020 levels, totaling $118 million
              relative to a $206 million limit. The underspending reflects the
              travel moratorium, as well as capacity constraints in recipient
              countries.
            </p>
            <MobileOnly>
              <Image path="who-we-are/resources/resources-1.jpg" />
            </MobileOnly>
          </Column>
        </PageColumns>

        {/* Income Model */}
        <PageColumns>
          <Column type="text">
            <h2>
              Income model, charges, remuneration, burden sharing, and total
              comprehensive income
            </h2>
            <h3>Income model</h3>
            <p>
              The IMF’s income is generated primarily through its lending and
              investing activities (Figure 3.1). Lending income is derived from
              the charges levied on the use of credit from the General Resources
              Account (GRA), service charges, and commitment fees. In addition,
              the use of IMF credit is subject to surcharges under certain
              circumstances. The IMF income model also relies on investment
              income generated from assets in the Fixed-Income and Endowment
              Subaccounts of the IMF’s Investment Account. Given the public
              nature of the funds, the IMF’s investment policy includes, among
              other things, a careful assessment of acceptable levels of risk as
              well as safeguards to minimize actual or perceived conflicts of
              interest.
            </p>

            <MobileOnly>
              <StaticChart
                figureNumber="3.1"
                title="Income Model"
                ariaLabel="Diagram showing how the IMF generates income mostly from lending and investing activities, and uses that income on its expenses, paying interest on its own financing, and saving for the future"
                source="Source: IMF, Finance Department. <br/> Note: Green boxes represent elements that were added to the income model in 2008. <br/> ¹As of April 30, 2021, the dividend policy has not been adopted by the membership."
                scaling={true}
                paddingbottom="210%"
              >
                <ChartIncomeModelMobile />
              </StaticChart>
            </MobileOnly>

            <h3>Charges</h3>
            <p>
              Reflecting the high levels of lending activities, the IMF’s main
              source of income continues to be{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05lev1sec01">
                charges
              </a>{" "}
              levied on outstanding credit. The basic rate of charge (the
              interest rate) on IMF financing comprises the special drawing
              right (SDR) interest rate plus a fixed margin expressed in basis
              points, as discussed in Part 2. In April 2020, the Executive Board
              set the margin for the rate of charge at 100 basis points for the
              period through April 2022.
            </p>
            <p>
              The IMF also levies{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05bx02">
                surcharges
              </a>{" "}
              on large amounts of credit. Surcharges apply to amounts of credit
              outstanding that exceed a defined threshold relative to a member’s
              quota (level-based surcharges), and they are higher when this
              threshold has been exceeded for a defined period of time
              (time-based surcharges) (see Table 2.2).
            </p>
            <p>
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05lev1sec01">
                In addition to charges and surcharges
              </a>
              , the IMF levies service charges, commitment fees, and special
              charges. A service charge of 0.5 percent is levied on each drawing
              from the GRA. A refundable{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05bx03">
                commitment fee
              </a>{" "}
              is charged at the beginning of each 12-month period on amounts
              available for drawing under GRA arrangements during that period.
              The IMF also levies{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch06.xml#ch06lev2sec07">
                special charges
              </a>
              , on charges that are past due, for the first six months that a
              member is in arrears.
            </p>

            <MobileOnly>
              <Image path="who-we-are/resources/resources-2.jpg" />
            </MobileOnly>

            <h3>Remuneration and interest on borrowing</h3>
            <p>
              On the expenditure side, the IMF pays interest (remuneration) to
              members on their creditor positions in the GRA (known as
              remunerated reserve tranche positions). The basic rate of
              remuneration is equal to the SDR interest rate. The IMF also pays
              interest at the SDR interest rate on outstanding borrowing under
              the New Arrangements to Borrow (NAB; see “IMF Financing”).
            </p>
            <AppendixWaypoint page={PAGESLUG} id={4} />

            <h3>Burden sharing</h3>
            <p>
              The rates of charge and remuneration are adjusted under a
              burden-sharing mechanism that distributes the cost of overdue
              financial obligations equally between debtor and creditor members.
            </p>

            <h3>Total comprehensive income</h3>
            <p>
              The IMF’s total comprehensive income in FY 2021 was SDR 4.8
              billion ($6.9 billion), reflecting primarily income from the high
              levels of lending activity, endowment income, and gains stemming
              from the remeasurement of the IMF’s employee benefit plans’ assets
              and liabilities, in accordance with International Financial
              Reporting Standards (International Accounting Standard 19,
              Employee Benefits).
            </p>

            <h3>Arrears to the IMF</h3>
            <p>
              Overdue financial obligations to the IMF amounted to SDR 964.3
              million at the end of April 2021 (see Table 3.1). At that time,
              Sudan¹ remained in protracted arrears (outstanding for more than
              six months) to the IMF, dating back to 1984.
            </p>

            <p>
              Under the IMF’s strengthened cooperative strategy on arrears,
              remedial measures have been taken to address the protracted
              arrears. At the end of the financial year, Sudan remained
              ineligible to use IMF resources.
            </p>

            <ol className="text-block__footnotes">
              <li>
                Sudan cleared its arrears to the IMF on June 29, 2021 (after
                this report was finalized). For more information, visit{" "}
                <a href="https://www.imf.org/sudan">www.imf.org/sudan</a>.
              </li>
            </ol>

            <MobileOnly>{table3_1}</MobileOnly>
          </Column>

          <Column type="media" classes="page-columns__column--space-around">
            <StaticChart
              figureNumber="3.1"
              title="Income Model"
              ariaLabel="Diagram showing how the IMF generates income mostly from lending and investing activities, and uses that income on its expenses, paying interest on its own financing, and saving for the future"
              source="Source: IMF, Finance Department. <br/> Note: Green boxes represent elements that were added to the income model in 2008. <br/> ¹As of April 30, 2021, the dividend policy has not been adopted by the membership."
              scaling={false}
            >
              <ChartIncomeModelDesktop />
            </StaticChart>

            <Image path="who-we-are/resources/resources-2.jpg" />

            {table3_1}
          </Column>
        </PageColumns>

        {/* IMF Financing */}
        <PageColumns modifiers="md:pull--large">
          <Column type="media">
            <ChartMultipleRowsDesktop />
          </Column>

          <Column type="text">
            <h2>IMF Financing</h2>
            <p>
              The IMF provides financing to its members through three channels,
              all of which serve the common purpose of transferring reserve
              currencies to member countries: regular (nonconcessional) lending
              from the GRA, concessional lending from the PRGT, and the SDR
              Department. The most salient feature of the IMF’s financial
              structure is that it is continuously evolving. The IMF has
              introduced and refined a variety of lending facilities and
              policies over the years to address changing conditions in the
              global economy or the specific needs and circumstances of its
              members.
            </p>

            <h3>Quotas: Where the IMF gets its money</h3>
            <p>
              The IMF’s 190 member countries provide resources for loans
              primarily through their payment of{" "}
              <a href="https://www.imf.org/en/About/Factsheets/Sheets/2016/07/14/12/21/IMF-Quotas">
                quotas
              </a>
              , which also set their voting rights. Multilateral borrowing and
              bilateral borrowing serve as a second and third line of defense in
              times of crisis. These resources give the IMF access to about $1
              trillion in nonconcessional lending firepower to support members.
              Concessional lending and debt relief for low-income countries is
              financed through separate contribution-based trust funds.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={7} />

            <p>
              Each member is assigned a quota based broadly on its position in
              the world economy. IMF quotas total SDR 477 billion (about $685
              billion). The value of the SDR, the IMF’s unit of account, is
              based on a basket of currencies (see “Special Drawing Right”).{" "}
            </p>

            <p>
              IMF quotas are also reviewed regularly. The 16th General Review of
              Quotas, which is underway, is an opportunity to assess the overall
              adequacy of the quotas as well as the adequacy of their
              distribution among IMF member countries. It will build on
              governance reforms of the 2010 review, including efforts to
              protect quotas and voting shares of the poorest members. The
              current formula for determining quotas, which was agreed upon in
              2008 and has been used as a guide, is also under review.{" "}
            </p>

            <MobileOnly>
              <div className="chart--centered">
                <ChartMultipleRowsMobile />
              </div>
            </MobileOnly>

            <h3>Quota payments </h3>
            <p>
              The conditions for implementing the quota increases agreed upon
              under the 14th General Quota Review were met on January 26, 2016.
              This resulted in a doubling of quota resources, to SDR 477 billion
              (about $685 billion) from about SDR 238.5 billion (about $342.5
              billion). As of April 30, 2021, 185 of the 190 members had made
              their quota payments, accounting for more than 99 percent of the
              total quota increases, and total quotas stood at SDR 476 billion
              (about $683 billion).
            </p>

            <h3>Andorra becomes 190th IMF member country</h3>
            <p>
              The{" "}
              <a href="https://www.imf.org/en/News/Articles/2020/10/16/pr20315-andorra-principality-of-andorra-becomes-imfs-190th-member">
                Principality of Andorra joined the IMF
              </a>{" "}
              on October 16, 2020, at a ceremony held in Washington, DC.{" "}
            </p>
          </Column>
        </PageColumns>

        {/* Borrowing by the IMF  */}
        <PageColumns modifiers="md:pull--small">
          <Column type="text">
            <h2>Borrowing by the IMF </h2>

            <h3>GRA borrowing</h3>
            <p>
              As noted, the IMF is a quota-based institution. However, borrowed
              resources continue to play a key role in supplementing quota
              resources through the NAB and the bilateral borrowing agreements
              (BBAs), serving respectively as a second and third line of defense
              after quotas.
            </p>
            <p>
              The NAB are a set of credit arrangements with 38 participants and
              2 prospective participants. The size of the NAB was doubled to
              about SDR 361 billion on January 1, 2021, and a new NAB period was
              set through the end of 2025. NAB resources can be activated when
              the IMF’s resources need to be supplemented to forestall or cope
              with an impairment of the international monetary system.
              Activation requires the consent of participants representing 85
              percent of total credit arrangements of participants eligible to
              vote and the approval of the Executive Board. The NAB were
              activated 10 times between April 2011 and February 2016, the last
              activation.
            </p>

            <MobileOnly>
              <Image path="who-we-are/resources/resources-3.jpg" />
            </MobileOnly>

            <p>
              As noted, BBAs are intended to serve as a third line of defense
              after quotas and the NAB. The current (2020) round of BBAs has
              been in effect since January 1, 2021, with an initial term through
              December 31, 2023, which may be extended by one more year. As of
              April 30, 2021, 40 bilateral creditors have committed under their
              2020 BBAs to provide the IMF with a total credit amount equivalent
              to about SDR 135 billion. Resources under BBAs can be activated
              only if the amount of the IMF resources otherwise available for
              financing has fallen below a threshold of SDR 100 billion and
              either the NAB is activated or there are no available uncommitted
              NAB resources. Activation of BBAs requires approval by bilateral
              creditors representing 85 percent of the total credit amount
              committed.
            </p>

            <h3>Special Drawing Right</h3>
            <AppendixWaypoint page={PAGESLUG} id={9} />
            <p>
              The{" "}
              <a href="https://www.imf.org/en/About/FAQ/special-drawing-right">
                special drawing right
              </a>{" "}
              is an international reserve asset created by the IMF in 1969 to
              supplement its member countries’ official reserves. IMF members
              who are participants in the SDR Department (currently all members)
              may exchange SDRs for freely usable currencies. The SDR serves as
              the unit of account of the IMF and some other international
              organizations. The SDR is neither a currency nor a claim on the
              IMF. Rather, it is a potential claim on the freely usable
              currencies of IMF members. SDRs can be exchanged for these
              currencies.{" "}
            </p>

            <p>
              The value of the SDR is currently based on a basket of five
              currencies: the US dollar, the euro, the Chinese renminbi, the
              Japanese yen, and the British pound. The currencies included are
              reviewed periodically; the next review of the valuation of the SDR
              basket is scheduled to occur by the end of July 2022.{" "}
            </p>

            <MobileOnly>
              <Image path="who-we-are/resources/resources-4.jpg" />
            </MobileOnly>

            <p>
              As of April 30, 2021, a total of SDR 204.2 billion (equivalent to
              about $293.2 billion) has been allocated to members so far,
              including SDR 182.6 billion allocated in 2009 in the wake of the
              global financial crisis. At an informal meeting in March 2021, the
              Executive Directors conveyed broad support among IMF members for a
              proposal by the Managing Director for a possible new SDR
              allocation. Once the Executive Board concurs with the Managing
              Director’s proposal, the proposal is submitted to the IMF’s Board
              of Governors, whose decision approving an SDR allocation requires
              support by members representing an 85 percent majority of the
              total voting power.
            </p>
          </Column>

          <Column type="media">
            <Image path="who-we-are/resources/resources-3.jpg" />
            <Image path="who-we-are/resources/resources-4.jpg" />
          </Column>
        </PageColumns>
      </section>
      <Appendix page={PAGESLUG} />
      <ReadNext sectionID="who-we-are" pageID="accountability" />
    </Layout>
  )
}
